import * as React from "react";
import {
  CandlestickChart,
  FolderKanbanIcon,
  Globe,
  Home,
  KeyRound,
  LineChart,
  MailPlusIcon,
  SunDim,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import { ClientSwitcher } from "@/components/Sidebar/ClientSwitcher";
import { NavMain } from "@/components/Sidebar/nav-main";
import { NavSettings } from "@/components/Sidebar/nav-settings";
import { NavUser } from "@/components/Sidebar/nav-user";
import { useAuth } from "@/hooks/useAuth";
import { checkPermission } from "@/lib/utils";

export const CLIENT_PORTAL_NAV_ITEMS = [
  {
    title: "Sparkspread",
    icon: LineChart,
    url: "/sparkspread-dashboard",
    permission: "sparkspread:read",
  },
  {
    title: "Clientsheet",
    icon: Home,
    url: "/clientsheets",
    permission: "clientsheet:read",
  },
  {
    title: "Pricesheet",
    icon: CandlestickChart,
    url: "/pricesheet",
    permission: "pricesheet:read",
  },
  {
    title: "Invoices",
    icon: FolderKanbanIcon,
    url: "/invoices",
    permission: "invoice:read",
  },
  {
    title: "Price Update",
    icon: MailPlusIcon,
    url: "/price-update",
    permission: "price_update:write",
  },
] as const;

export const SETTINGS_NAV_ITEMS = [
  {
    title: "API Access",
    icon: KeyRound,
    url: "/client-credentials",
  },
  {
    title: "Language Settings",
    icon: Globe,
    url: "/language-settings",
  },
];

function AppBrand() {
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton size="lg">
          <div className="sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
            <SunDim className="size-6" />
          </div>
          <div className="grid flex-1 text-left text-sm leading-tight">
            <span className="truncate font-semibold">BCI - Client Portal</span>
          </div>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user } = useAuth();
  const allowedNavItems = CLIENT_PORTAL_NAV_ITEMS.filter((item) => {
    try {
      checkPermission(item.permission);
      return true;
    } catch {
      return false;
    }
  });

  return (
    <Sidebar collapsible="icon" {...props} variant="inset">
      <SidebarHeader>
        <AppBrand />
        <ClientSwitcher />
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={allowedNavItems} />
        <NavSettings items={SETTINGS_NAV_ITEMS} />
      </SidebarContent>
      <SidebarFooter>
        <NavUser
          user={{
            email: user?.email ?? "",
            name: `${user?.first_name ?? ""} ${user?.last_name ?? ""}`,
          }}
        />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
